import React, {useEffect, useState} from 'react';
import {useNavigate} from "react-router-dom";
import {addTournament, deleteTournament, fetchAdminTournaments, getUserData, updateTournament, sendTournamentEmail} from "../../api/api";
import styles from './AdminTournaments.module.css'
import {formatDateTime} from "../../utils";
import CustomModal from "../../UI/Modal/CustomModal";
import Spinner from "../Spinner/Spinner";

const initialDescription = `
    Zapraszamy do tenisowego współzawodnictwa 🔥, 
    gdzie w przyjaznej atmosferze spędzisz niezapomniane chwile podczas sportowej rywalizacji na korcie.
    <br/><br/>

    🎾 Gwarantowane minimum <strong>2 mecze</strong> dla każdego uczestnika.
    <br/><br/>

    <strong>📅 Format rozgrywek</strong><br/>
    Turniej będzie rozgrywany <strong>w formule jednodniowej</strong>. System rywalizacji będzie zależał od liczby zgłoszonych graczy / par deblowych:
    <br/><br/>

    <strong>✅ 4–5 graczy</strong><br/>
    <a href='https://btt.org.pl/competition-descriptions' target='_blank'>System „każdy z każdym”</a><br/><br/>

    <strong>✅ 6–12 graczy</strong><br/>
    <a href='https://btt.org.pl/competition-descriptions' target='_blank'>System „faza grupowa + faza finałowa”</a><br/><br/>

    <strong>✅ 13–16 graczy</strong><br/>
    <a href='https://btt.org.pl/competition-descriptions' target='_blank'>System „brazylijski do dwóch przegranych meczów”</a><br/><br/>

    <strong>🏆 Nagrody:</strong> Piłki tenisowe 🎾🎾🎾<br/><br/>

    <strong>💸 Wpisowe od osoby:</strong><br/>
    &nbsp;&nbsp;&nbsp;&nbsp;• <strong>40 zł</strong> – członkowie BTT<br/>
    &nbsp;&nbsp;&nbsp;&nbsp;• <strong>60 zł</strong> – pozostali gracze<br/>
    Wpisowe naliczane automatycznie na koncie gracza w momencie rozpoczęcia turnieju.
    <br/>
`;


const AdminTournaments = () => {
    const [tournaments, setTournaments] = useState([]);
    const [selectedTournament, setSelectedTournament] = useState(null);
    const [title, setTitle] = useState('');
    const [participantLimit, setParticipantLimit] = useState('');
    const [numberOfPointsInSet, setNumberOfPointsInSet] = useState('');
    const [tournamentEntryFeeMember, setTournamentEntryFeeMember] = useState('');
    const [tournamentEntryFeeNonMember, setTournamentEntryFeeNonMember] = useState('');
    const [leagueMatchFee, setLeagueMatchFee] = useState('');
    const [isThirdSetTiebreak, setIsThirdSetTiebreak] = useState(false);
    const [isLeague, setIsLeague] = useState(false);
    const [eloEnabled, setEloEnabled] = useState(true);
    const [friendly, setFriendly] = useState(false);
    const [friendlyToken, setFriendlyToken] = useState('');
    const [randomDoublesDraw, setRandomDoublesDraw] = useState(false);
    const [matchFormat, setMatchFormat] = useState('BEST_OF_3_TO_4');
    const [description, setDescription] = useState(initialDescription);
    const [tournamentDate, setTournamentDate] = useState('');
    const [registrationStartDate, setRegistrationStartDate] = useState('');
    const [registrationEndDate, setRegistrationEndDate] = useState('');
    const [tournamentStatus, setTournamentStatus] = useState('');
    const [tournamentType, setTournamentType] = useState('');
    const [tournamentGender, setTournamentGender] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showConfirmButton, setShowConfirmButton] = useState(false);
    const [modalMessage, setModalMessage] = useState("");
    const [confirmLabel, setConfirmLabel] = useState("");
    const [showForm, setShowForm] = useState(false);
    const [userData, setUserData] = useState({});
    const [userFetched, setUserFetched] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [emailModal, setEmailModal] = useState(null); // { tournamentId, tournamentName }
    const [emailSubject, setEmailSubject] = useState('');
    const [emailBody, setEmailBody] = useState('');
    const [isSendingEmail, setIsSendingEmail] = useState(false);


    useEffect(() => {
        const fetchData = async () => {
            try {
                const data = await getUserData();
                setUserData(data);
            } catch (e) {
                console.error('Failed to fetch user data:', e);
            } finally {
                setUserFetched(true);
            }
        };
        fetchData();
    }, []);

    const canUserSeeTournaments = () => {
        const userRoles = new Set(userData.authorities || []);
        return userRoles.has("ROLE_ADMIN") || userRoles.has("ROLE_SUPERVISOR");
    };

    const navigate = useNavigate();

    const handleOpenEmailModal = (tournamentId, tournamentName) => {
        setEmailSubject(tournamentName);
        setEmailBody('');
        setEmailModal({ tournamentId, tournamentName });
    };

    const handleSendEmail = async () => {
        if (!emailSubject.trim() || !emailBody.trim()) {
            return;
        }
        setIsSendingEmail(true);
        const htmlMessage = emailBody.replace(/\n/g, '<br>');
        const result = await sendTournamentEmail(emailModal.tournamentId, emailSubject, htmlMessage);
        setIsSendingEmail(false);
        setEmailModal(null);
        if (result.success) {
            setModalMessage('Wiadomość wysłana do uczestników turnieju');
            setShowConfirmButton(false);
            setIsModalOpen(true);
        } else {
            setModalMessage('Błąd wysyłki: ' + (result.data?.message || result.data || 'Nieznany błąd'));
            setShowConfirmButton(false);
            setIsModalOpen(true);
        }
    };

    const sortTournaments = (tournaments) => {
        return tournaments.sort((a, b) => new Date(b.tournamentDate) - new Date(a.tournamentDate));
    };


    useEffect(() => {
        const fetchData = async () => {
            // Gate na userFetched — bez tego pierwsze sprawdzenie roli leciało na pustym userData
            // i fetch turniejów rzucał 403 dla nie-adminów (catch pusty → isLoading wisiało na true → spinner forever).
            if (!userFetched) return;
            if (!canUserSeeTournaments()) {
                setIsLoading(false);
                return;
            }
            try {
                const result = await fetchAdminTournaments();
                if (result.success) {
                    const sortedTournaments = sortTournaments(result.data);
                    setTournaments(sortedTournaments);
                }
            } catch (e) {
                console.error('Failed to fetch tournaments:', e);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [userFetched]);

    const handleAddTournament = async () => {
        const result = await addTournament({
            title,
            participantLimit,
            numberOfPointsInSet: numberOfPointsInSet,
            tournamentEntryFeeMember: tournamentEntryFeeMember,
            tournamentEntryFeeNonMember: tournamentEntryFeeNonMember,
            leagueMatchFee: leagueMatchFee,
            isThirdSetTiebreak,
            isLeague,
            eloEnabled,
            friendly,
            randomDoublesDraw,
            matchFormat,
            description,
            tournamentDate,
            registrationStartDate,
            registrationEndDate,
            status: tournamentStatus,
            type: tournamentType,
            gender: tournamentGender,
        });
        if (result.success) {
            const sortedTournaments = sortTournaments(result.data);
            setTournaments(sortedTournaments);
            setShowForm(false);
            setTitle('');
            setParticipantLimit('');
            setNumberOfPointsInSet('');
            setTournamentEntryFeeMember('');
            setTournamentEntryFeeNonMember('');
            setLeagueMatchFee('');
            setIsThirdSetTiebreak(false);
            setIsLeague(false);
            setEloEnabled(true);
            setFriendly(false);
            setFriendlyToken('');
            setRandomDoublesDraw(false);
            setMatchFormat('BEST_OF_3_TO_4');
            setDescription(initialDescription);
            setTournamentDate('');
            setRegistrationStartDate('');
            setRegistrationEndDate('');
            setTournamentStatus('');
            setTournamentType('');
            setTournamentGender('');
        }
    };

    const handleUpdateTournament = async () => {
        const result = await updateTournament(selectedTournament.id, {
            title,
            participantLimit,
            numberOfPointsInSet: numberOfPointsInSet,
            tournamentEntryFeeMember: tournamentEntryFeeMember,
            tournamentEntryFeeNonMember: tournamentEntryFeeNonMember,
            leagueMatchFee: leagueMatchFee,
            isThirdSetTiebreak,
            isLeague,
            eloEnabled,
            friendly,
            randomDoublesDraw,
            matchFormat,
            description,
            tournamentDate,
            registrationStartDate,
            registrationEndDate,
            status: tournamentStatus,
            type: tournamentType,
            gender: tournamentGender,
        });
        if (result.success) {
            const sortedTournaments = sortTournaments(result.data);
            setTournaments(sortedTournaments);
            setShowForm(false);
            setSelectedTournament(null);
            setTitle('');
            setParticipantLimit('');
            setNumberOfPointsInSet('');
            setTournamentEntryFeeMember('');
            setTournamentEntryFeeNonMember('');
            setLeagueMatchFee('');
            setIsThirdSetTiebreak(false);
            setIsLeague(false);
            setEloEnabled(true);
            setFriendly(false);
            setFriendlyToken('');
            setRandomDoublesDraw(false);
            setMatchFormat('BEST_OF_3_TO_4');
            setDescription(initialDescription);
            setTournamentDate('');
            setRegistrationStartDate('');
            setRegistrationEndDate('');
            setTournamentStatus('');
            setTournamentType('');
            setTournamentGender('');
        }
    };

    const handleDeleteTournament = async (tournament) => {
        setSelectedTournament(tournament);
        setModalMessage("Czy napewo chcesz usunąć: " + tournament.title);
        setConfirmLabel("Usuwam");
        setShowConfirmButton(true);
        setIsModalOpen(true)
    };

    const handleConfirmAction = async () => {
        const id = selectedTournament.id;
        const result = await deleteTournament(id);
        if (result.success) {
            setTournaments(tournaments.filter((tournament) => tournament.id !== id));
            setModalMessage("Turniej usunięty");
            setShowConfirmButton(false);
            setIsModalOpen(true)
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const handleStartTournament = (tournamentId) => {
        navigate(`/start-tournament/${tournamentId}`);
    };

    if(isLoading) return <Spinner size={"large"}/>

    return (
        <>
            {canUserSeeTournaments() ? (
                    <div className={styles.formContainer}>
                        <CustomModal
                            isOpen={isModalOpen}
                            onRequestClose={handleCloseModal}
                            message={modalMessage}
                            confirmLabel={showConfirmButton ? confirmLabel : undefined}
                            onConfirm={showConfirmButton ? handleConfirmAction : undefined}
                            isPositiveMessage={modalMessage.includes("Dodano") || modalMessage.includes("anulowane")
                                || modalMessage.includes("Zapisano") || modalMessage.includes("usunięty")
                                || modalMessage.includes("wysłana") || modalMessage.includes("skopiowany")}
                            cancelLabel="Zamknij"
                            confirmButtonColor="red"
                            cancelButtonColor="green"
                        />
                        <div>
                            <button className={styles.addTournament} onClick={() => setShowForm(!showForm)}>
                                {showForm ? "Zamknij formularz" : "Nowy turniej"}
                            </button>
                        </div>
                        {showForm && (
                            <div>
                                <div className={styles.formRow}>
                                    <label>
                                        Nazwa turnieju:
                                        <input
                                            className={styles.input}
                                            value={title}
                                            onChange={(e) => setTitle(e.target.value)}/>
                                    </label>
                                </div>
                                <div className={styles.formRow}>
                                    <label>
                                        Typ:
                                        <select
                                            className={styles.input}
                                            value={tournamentType}
                                            onChange={(e) => setTournamentType(e.target.value)}
                                        >
                                            <option value="">Select type</option>
                                            <option value="SINGLES">Singles</option>
                                            <option value="DOUBLES">Doubles</option>
                                        </select>
                                    </label>
                                </div>
                                <div className={styles.formRow}>
                                    <label>
                                        Rodzaj:
                                        <select
                                            className={styles.input}
                                            value={tournamentGender}
                                            onChange={(e) => setTournamentGender(e.target.value)}
                                        >
                                            <option value="">Select type</option>
                                            <option value="MALE">Męski</option>
                                            <option value="FEMALE">Kobiecy</option>
                                        </select>
                                    </label>
                                </div>
                                <div className={styles.formRow}>
                                    <label>
                                        Limit uczestników:
                                        <input
                                            className={styles.input}
                                            type="number"
                                            value={participantLimit}
                                            onChange={(e) => setParticipantLimit(e.target.value)}/>
                                    </label>
                                </div>
                                <div className={styles.formRow}>
                                    <label>
                                        Ilość gemów w secie:
                                        <input
                                            className={styles.input}
                                            type="number"
                                            value={numberOfPointsInSet}
                                            onChange={(e) => setNumberOfPointsInSet(e.target.value)}/>
                                    </label>
                                </div>
                                <div className={styles.formRow}>
                                    <label>
                                        Wpisowe BTT:
                                        <input
                                            className={styles.input}
                                            type="number"
                                            value={tournamentEntryFeeMember}
                                            onChange={(e) => setTournamentEntryFeeMember(e.target.value)}/>
                                    </label>
                                </div>
                                <div className={styles.formRow}>
                                    <label>
                                        Wpisowe Pozostali:
                                        <input
                                            className={styles.input}
                                            type="number"
                                            value={tournamentEntryFeeNonMember}
                                            onChange={(e) => setTournamentEntryFeeNonMember(e.target.value)}/>
                                    </label>
                                </div>
                                <div className={styles.formRow}>
                                    <label>
                                        Oplata za mecz ligowy:
                                        <input
                                            className={styles.input}
                                            type="number"
                                            value={leagueMatchFee}
                                            onChange={(e) => setLeagueMatchFee(e.target.value)}/>
                                    </label>
                                </div>
                                <div className={styles.formRow}>
                                    <label>
                                        Format meczu:
                                        <select
                                            className={styles.input}
                                            value={matchFormat}
                                            onChange={(e) => setMatchFormat(e.target.value)}>
                                            <option value="BEST_OF_3_TO_4">Best-of-3</option>
                                            <option value="PRO_SET_TO_9_TB_AT_8">Pro-set do 9 gemów (tiebreak przy 8:8)</option>
                                            <option value="PRO_SET_TO_7_TB_AT_6">Pro-set do 7 gemów (tiebreak przy 6:6)</option>
                                            <option value="PRO_SET_TO_6_TB_AT_5">Pro-set do 6 gemów (tiebreak przy 5:5)</option>
                                        </select>
                                    </label>
                                </div>
                                <div className={styles.formRow}>
                                    <label>
                                        Tiebreak w 3 secie ?
                                        <input
                                            type="checkbox"
                                            checked={isThirdSetTiebreak}
                                            onChange={(e) => setIsThirdSetTiebreak(e.target.checked)}
                                            className={styles.checkbox}/>
                                    </label>
                                </div>
                                <div className={styles.formRow}>
                                    <label>
                                        Liga ?
                                        <input
                                            type="checkbox"
                                            checked={isLeague}
                                            onChange={(e) => setIsLeague(e.target.checked)}
                                            className={styles.checkbox}/>
                                    </label>
                                </div>
                                <div className={styles.formRow}>
                                    <label>
                                        Elo ?
                                        <input
                                            type="checkbox"
                                            checked={eloEnabled}
                                            onChange={(e) => setEloEnabled(e.target.checked)}
                                            className={styles.checkbox}/>
                                    </label>
                                </div>
                                <div className={styles.formRow}>
                                    <label title="Turniej towarzyski — ukryty z publicznej listy, dostępny tylko dla osób ze specjalnym linkiem">
                                        Towarzyski ?
                                        <input
                                            type="checkbox"
                                            checked={friendly}
                                            onChange={(e) => setFriendly(e.target.checked)}
                                            className={styles.checkbox}/>
                                    </label>
                                </div>
                                {tournamentType === 'SINGLES' && (
                                    <div className={styles.formRow}>
                                        <label title="Turniej zaczyna jako singlowy (zapisy pojedynczych graczy), pary deblowe losowane są w panelu Ligi Grupowej przed startem">
                                            Losowanie par deblowych ?
                                            <input
                                                type="checkbox"
                                                checked={randomDoublesDraw}
                                                onChange={(e) => setRandomDoublesDraw(e.target.checked)}
                                                className={styles.checkbox}/>
                                        </label>
                                    </div>
                                )}
                                {friendly && friendlyToken && (
                                    <div className={styles.formRow}>
                                        <label>Link do zapisów:</label>
                                        <input
                                            id={`friendly-link-${friendlyToken}`}
                                            className={styles.input}
                                            type="text"
                                            value={`${window.location.origin}/tournaments/friendly/${friendlyToken}`}
                                            readOnly
                                            onFocus={(e) => e.target.select()}
                                        />
                                        <button
                                            type="button"
                                            className={styles.buttonStart}
                                            onClick={async () => {
                                                const link = `${window.location.origin}/tournaments/friendly/${friendlyToken}`;
                                                let copied = false;
                                                try {
                                                    if (navigator.clipboard && window.isSecureContext) {
                                                        await navigator.clipboard.writeText(link);
                                                        copied = true;
                                                    }
                                                } catch (err) {
                                                    copied = false;
                                                }
                                                if (!copied) {
                                                    const input = document.getElementById(`friendly-link-${friendlyToken}`);
                                                    if (input) {
                                                        input.focus();
                                                        input.select();
                                                        try {
                                                            copied = document.execCommand('copy');
                                                        } catch (err) {
                                                            copied = false;
                                                        }
                                                    }
                                                }
                                                setModalMessage(copied
                                                    ? 'Link skopiowany do schowka'
                                                    : 'Nie udało się skopiować — zaznacz pole i użyj Ctrl+C');
                                                setShowConfirmButton(false);
                                                setIsModalOpen(true);
                                            }}
                                        >
                                            Kopiuj
                                        </button>
                                    </div>
                                )}
                                <div className={styles.formRow}>
                                    <label>
                                        Opis:
                                        <textarea
                                            className={styles.textarea}
                                            value={description}
                                            onChange={(e) => setDescription(e.target.value)}/>
                                    </label>
                                </div>
                                <div className={styles.formRow}>
                                    <label>
                                        Data turnieju:
                                        <input
                                            className={styles.input}
                                            type="datetime-local"
                                            value={tournamentDate}
                                            onChange={(e) => setTournamentDate(e.target.value)}/>
                                    </label>
                                </div>
                                <div className={styles.formRow}>
                                    <label>
                                        Start zapisów:
                                        <input
                                            className={styles.input}
                                            type="datetime-local"
                                            value={registrationStartDate}
                                            onChange={(e) => setRegistrationStartDate(e.target.value)}/>
                                    </label>
                                </div>
                                <div className={styles.formRow}>
                                    <label>
                                        Koniec zapisów:
                                        <input
                                            className={styles.input}
                                            type="datetime-local"
                                            value={registrationEndDate}
                                            onChange={(e) => setRegistrationEndDate(e.target.value)}/>
                                    </label>
                                </div>
                                <div className={styles.formRow}>
                                    <label>
                                        Status:
                                        <select
                                            className={styles.input}
                                            value={tournamentStatus}
                                            onChange={(e) => setTournamentStatus(e.target.value)}
                                        >
                                            <option value="">Select status</option>
                                            <option value="PLANNED">Zaplanowany</option>
                                            <option value="ACTIVE">Trwający</option>
                                            <option value="COMPLETED">Zakończony</option>
                                            <option value="HOLDED">Wstrzymany</option>
                                        </select>
                                    </label>
                                </div>
                                <div>
                                    <button
                                        className={selectedTournament ? styles.buttonUpdate : styles.buttonAdd}
                                        onClick={selectedTournament ? handleUpdateTournament : handleAddTournament}
                                    >
                                        {selectedTournament ? 'Aktualizuj turniej' : 'Dodaj turniej'}
                                    </button>
                                </div>
                            </div>
                        )}
                        <div className={styles.tableContainer}>
                            <table className={styles.table}>
                                <thead>
                                <tr>
                                    <th>Id</th>
                                    <th>Tytuł</th>
                                    <th>Akcje</th>
                                    <th>Typ</th>
                                    <th>Rodzaj</th>
                                    <th>Status</th>
                                    <th>Limit</th>
                                    <th>Gemy</th>
                                    <th>Wpisowe BTT</th>
                                    <th>Wpisowe inni</th>
                                    <th>opłata ligowa</th>
                                    <th>Data</th>
                                    <th>Start zapisów</th>
                                    <th>Koniec zapisów</th>
                                </tr>
                                </thead>
                                <tbody>
                                {tournaments.map((tournament) => (
                                    <tr key={tournament.id}>
                                        <td>{tournament.id}</td>
                                        <td>{tournament.title}</td>
                                        <td className={styles.actions}>
                                            {(tournament.status === 'PLANNED' || tournament.status === 'ACTIVE') && (
                                                <>
                                                    <button
                                                        className={styles.buttonEdit}
                                                        onClick={() => {
                                                            setSelectedTournament(tournament);
                                                            setTitle(tournament.title);
                                                            setParticipantLimit(tournament.participantLimit.toString());
                                                            setNumberOfPointsInSet(tournament.numberOfPointsInSet.toString());
                                                            setTournamentEntryFeeMember(tournament.tournamentEntryFeeMember.toString());
                                                            setTournamentEntryFeeNonMember(tournament.tournamentEntryFeeNonMember.toString());
                                                            setLeagueMatchFee(tournament.leagueMatchFee);
                                                            setIsThirdSetTiebreak(tournament.isThirdSetTiebreak);
                                                            setIsLeague(tournament.isLeague);
                                                            setEloEnabled(tournament.eloEnabled !== false);
                                                            setFriendly(tournament.friendly === true);
                                                            setFriendlyToken(tournament.friendlyToken || '');
                                                            setRandomDoublesDraw(tournament.randomDoublesDraw === true);
                                                            setMatchFormat(tournament.matchFormat || 'BEST_OF_3_TO_4');
                                                            setDescription(tournament.description);
                                                            setTournamentDate(tournament.tournamentDate.slice(0, 16));
                                                            setRegistrationStartDate(tournament.registrationStartDate.slice(0, 16));
                                                            setRegistrationEndDate(tournament.registrationEndDate.slice(0, 16));
                                                            setTournamentStatus(tournament.status);
                                                            setTournamentType(tournament.type);
                                                            setTournamentGender(tournament.gender);
                                                            setShowForm(true);
                                                        }}
                                                    >
                                                        Edytuj
                                                    </button>
                                                </>
                                            )}
                                            {tournament.status === 'PLANNED' && (
                                                <>
                                                    <button
                                                        className={styles.buttonStart}
                                                        onClick={() => handleStartTournament(tournament.id)}
                                                    >
                                                        Start
                                                    </button>
                                                    <button className={`${styles.buttonDelete}`}
                                                            onClick={() => handleDeleteTournament(tournament)}>
                                                        Usuń
                                                    </button>
                                                </>
                                            )}
                                            {(tournament.status === 'PLANNED' || tournament.status === 'ACTIVE') && (
                                                <button className={styles.buttonMail}
                                                        onClick={() => handleOpenEmailModal(tournament.id, tournament.title)}>
                                                    Mail
                                                </button>
                                            )}
                                        </td>
                                        <td>{tournament.type}</td>
                                        <td>{tournament.gender}</td>
                                        <td>{tournament.status}</td>
                                        <td>{tournament.participantLimit}</td>
                                        <td>{tournament.numberOfPointsInSet}</td>
                                        <td>{tournament.tournamentEntryFeeMember}</td>
                                        <td>{tournament.tournamentEntryFeeNonMember}</td>
                                        <td>{tournament.leagueMatchFee}</td>
                                        <td>{formatDateTime(tournament.tournamentDate)}</td>
                                        <td>{formatDateTime(tournament.registrationStartDate)}</td>
                                        <td>{formatDateTime(tournament.registrationEndDate)}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) :
                (<p className={styles.access}>Brak uprawnień</p>)
            }

            {emailModal && (
                <div className={styles.emailOverlay} onClick={() => setEmailModal(null)}>
                    <div className={styles.emailModalContent} onClick={e => e.stopPropagation()}>
                        <h3>Wiadomość do uczestników: {emailModal.tournamentName}</h3>
                        <label className={styles.emailLabel}>Temat:</label>
                        <input
                            type="text"
                            className={styles.emailInput}
                            value={emailSubject}
                            onChange={e => setEmailSubject(e.target.value)}
                            placeholder="Temat wiadomości"
                        />
                        <label className={styles.emailLabel}>Treść:</label>
                        <textarea
                            className={styles.emailTextarea}
                            value={emailBody}
                            onChange={e => setEmailBody(e.target.value)}
                            rows={8}
                            placeholder="Treść wiadomości (nowe linie zostaną zachowane)"
                        />
                        <div className={styles.emailButtons}>
                            <button className={styles.buttonDelete} onClick={() => setEmailModal(null)}>Anuluj</button>
                            <button
                                className={styles.buttonStart}
                                onClick={handleSendEmail}
                                disabled={isSendingEmail || !emailSubject.trim() || !emailBody.trim()}
                            >
                                {isSendingEmail ? 'Wysyłanie...' : 'Wyślij'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default AdminTournaments;
